import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { loyaltyService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Loader2, Search, Check } from "lucide-react";

const INDUSTRIES = ["RETAIL", "HEALTHCARE", "FINANCE", "EDUCATION", "MANUFACTURING", "GOVERNMENT"];

export default function PackConfiguration() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("ENTITLEMENT.OVERRIDE");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [mapping, setMapping] = useState({});
  const [savingMap, setSavingMap] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await loyaltyService.packConfig();
      setData(d); setMapping(d.industryMapping || {});
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    setBusy(t.tenantId);
    try {
      await loyaltyService.togglePack(t.tenantId, !t.enabled);
      toast.success(`Loyalty pack ${!t.enabled ? "enabled" : "disabled"} for ${t.tenantName}`);
      setData((prev) => ({ ...prev, tenants: prev.tenants.map((x) => x.tenantId === t.tenantId ? { ...x, enabled: !t.enabled } : x) }));
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const saveMapping = async () => {
    setSavingMap(true);
    try { await loyaltyService.setIndustryMapping(mapping); toast.success("Industry mapping saved"); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSavingMap(false); }
  };

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const tenants = (data?.tenants || []).filter((t) => !q || (t.tenantName || "").toLowerCase().includes(q.toLowerCase()) || (t.tenantCode || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="pack-configuration-page">
      <PageHeader title="Pack Configuration" subtitle="Enable commercial packs per tenant — ReasonLoyaltyIQ™" />

      <div className="bg-card border border-border rounded-2xl shadow-sm p-5 mb-6" data-testid="pack-meta-card">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Boxes className="h-5.5 w-5.5" /></span>
          <div>
            <p className="font-semibold text-foreground">{data?.pack?.name} <span className="font-mono text-xs text-muted-foreground">({data?.pack?.packCode})</span></p>
            <p className="text-xs text-muted-foreground">{data?.pack?.tagline}</p>
          </div>
        </div>
      </div>

      {/* Industry availability mapping */}
      <div className="bg-card border border-border rounded-2xl shadow-sm p-5 mb-6" data-testid="industry-mapping-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground">Industry Pack Mapping</h3>
          {canEdit && (
            <button onClick={saveMapping} disabled={savingMap} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="mapping-save-button">
              {savingMap && <Loader2 className="h-4 w-4 animate-spin" />} Save Mapping
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">Configure which industries make Loyalty available (fully configurable, no hardcoding).</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {INDUSTRIES.map((ind) => (
            <label key={ind} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`mapping-${ind}`}>
              <input type="checkbox" checked={!!mapping[ind]} disabled={!canEdit} onChange={(e) => setMapping({ ...mapping, [ind]: e.target.checked })} className="accent-primary" />
              <span className="text-sm capitalize text-foreground">{ind.toLowerCase()}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Per-tenant enablement */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden" data-testid="tenant-enablement-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Tenant Enablement</h3>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tenants…" className="rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm w-56" data-testid="pack-tenant-search" />
          </div>
        </div>
        <div className="divide-y divide-border">
          {tenants.map((t) => (
            <div key={t.tenantId} className="flex items-center justify-between px-5 py-3" data-testid={`pack-tenant-row-${t.tenantCode}`}>
              <div>
                <p className="text-sm font-medium text-foreground">{t.tenantName} <span className="font-mono text-xs text-muted-foreground">{t.tenantCode}</span></p>
                <p className="text-xs text-muted-foreground">{t.industry || "—"}</p>
              </div>
              <div className="flex items-center gap-3">
                {t.enabled && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check className="h-3.5 w-3.5" /> Enabled</span>}
                <button onClick={() => toggle(t)} disabled={!canEdit || busy === t.tenantId}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${t.enabled ? "bg-primary" : "bg-muted"}`}
                  data-testid={`pack-toggle-${t.tenantCode}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${t.enabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
